/**
 * authCtx에서 공통 인증 정보를 추출
 */
function getAuthInfo(authCtx) {
  return {
    solSer: authCtx.userInfo.sol_ser || '',
    affSer: authCtx.userInfo.aff_ser || '',
    affNo: Number(authCtx.userInfo?.aff_no || 0),
    memberNo: authCtx.memberNo,
  };
}

/**
 * 버전 데이터에 대한 노출 권한을 확인
 * - authorized 목록 중 하나라도 해당되거나, 제한이 없으면 노출
 * - unrecognized 대상이면 미노출 (exempted_m_no 예외)
 */
function checkVersionAccess(version, { solSer, affSer, affNo, memberNo }) {
  const {
    authorized_m_no,
    authorized_aff_no,
    authorized_aff_ser,
    authorized_sol_ser,
    unrecognized_aff_no,
    exempted_m_no,
  } = version;

  // authorized 목록이 하나도 지정되지 않은 경우 → 모든 사용자에게 공개
  const hasNoRestriction =
    !authorized_m_no?.length &&
    !authorized_aff_no?.length &&
    !authorized_aff_ser?.length &&
    !authorized_sol_ser?.length;

  const isAuthorizedMember    = authorized_m_no?.length    ? authorized_m_no.includes(memberNo)               : false; // 허용된 회원번호에 포함되는지
  const isAuthorizedAffiliate = authorized_aff_no?.length  ? authorized_aff_no.includes(affNo)                : false; // 허용된 제휴사번호에 포함되는지
  const isAuthorizedAffSer    = authorized_aff_ser?.length ? authorized_aff_ser.some(s => affSer.includes(s)) : false; // 허용된 제휴사 서비스코드에 해당하는지
  const isAuthorizedSolSer    = authorized_sol_ser?.length ? authorized_sol_ser.some(s => solSer.includes(s)) : false; // 허용된 솔루션 서비스코드에 해당하는지
  const isUnrecognized        = unrecognized_aff_no?.includes(affNo) ?? false;                                         // 미인증(차단) 제휴사번호에 해당하는지
  const isExempted            = exempted_m_no?.length      ? exempted_m_no.includes(memberNo)                 : false; // authorized 조건과 무관하게 접근 허용된 회원인지

  // 하나라도 authorized 조건을 만족하거나 제한이 없고, 차단 대상이 아닌 경우 노출
  const isAuthorized = hasNoRestriction || isAuthorizedMember || isAuthorizedAffiliate || isAuthorizedAffSer || isAuthorizedSolSer;

  return (isAuthorized && !isUnrecognized) || isExempted;
}

/**
 * 사이드 네비게이션 바에 노출되는 항목들 필터링
 */
export function getFilteredPageDataList(authCtx, pageDataList) {
  const authInfo = getAuthInfo(authCtx);
  const { affNo, affSer, memberNo } = authInfo;

  return pageDataList
    .map(actions => {
      const filteredChildren = actions.children.filter(action => {
        // action.data가 없거나 빈 배열일 때 방어 처리
        if (!action.data || !action.data[0]) return false;

        // 에러코드 섹션 전용 조건
        if (actions.title === '에러코드') {
          const isUnrecognized  = action.unrecognized_aff_no?.includes(affNo);
          const hasExemptAffSer = action.unrecognized_aff_ser?.some(s => affSer.includes(s));
          return !(isUnrecognized && !hasExemptAffSer);
        }

        // 버전이 여러 개일 경우, 하나라도 노출 조건을 만족하면 메뉴를 노출
        return action.data.some(version => checkVersionAccess(version, authInfo));
      });

      return { ...actions, children: filteredChildren };
    })
    .filter(actions => {
      const hasVisibleChildren    = actions.children.length > 0 || actions.alwaysShow; // 노출할 하위 메뉴가 존재하거나 항상 표시하는 섹션인지
      const isUnrecognizedAff    = actions.unrecognized_aff_no?.includes(affNo);        // 섹션 단위에서 차단된 제휴사번호인지
      const isUnrecognizedAffSer = actions.unrecognized_aff_ser?.some(s => affSer.includes(s)); // 섹션 단위에서 차단된 제휴사 서비스코드인지
      const isExempted           = actions.exempted_m_no?.includes(memberNo);           // 섹션 단위 접근 허용된 회원인지

      return (hasVisibleChildren && !isUnrecognizedAff && !isUnrecognizedAffSer) || isExempted;
    });
}

/**
 * API 버전별 노출되는 항목들 필터링
 */
export function getPageDataRawFiltered(pageDataRaw, authCtx) {
  const authInfo = {
    ...getAuthInfo(authCtx),
    memberNo: Number(authCtx.memberNo || 0),
  };

  return {
    ...pageDataRaw,
    data: pageDataRaw.data.filter(version => checkVersionAccess(version, authInfo)),
  };
}

/**
 * 페이지별 접근 권한 검증
 */
export function getShouldValidateAccess(pageData, authCtx) {
  const solSer   = authCtx.userInfo.sol_ser || '';
  const affSer   = authCtx.userInfo.aff_ser || '';
  const affNo    = authCtx.affNo;
  const memberNo = authCtx.memberNo;

  const {
    authorized_m_no,
    authorized_aff_no,
    authorized_aff_ser,
    authorized_sol_ser,
    unrecognized_aff_no,
    unrecognized_aff_ser,
    exempted_m_no,
  } = pageData || {};

  // authorized 목록이 하나도 지정되지 않은 경우 → 모든 사용자에게 공개
  const hasNoRestriction =
    !authorized_m_no?.length &&
    !authorized_aff_no?.length &&
    !authorized_aff_ser?.length &&
    !authorized_sol_ser?.length;

  // 제한 목록이 없으면 기본 허용(true) — checkVersionAccess와 달리 미지정 시 허용으로 처리
  const isAuthorizedMember    = authorized_m_no?.length    ? authorized_m_no.includes(memberNo)               : true; // 허용된 회원번호에 포함되는지
  const isAuthorizedAffiliate = authorized_aff_no?.length  ? authorized_aff_no.includes(affNo)                : true; // 허용된 제휴사번호에 포함되는지
  const isAuthorizedAffSer    = authorized_aff_ser?.length ? authorized_aff_ser.some(s => affSer.includes(s)) : true; // 허용된 제휴사 서비스코드에 해당하는지
  const isAuthorizedSolSer    = authorized_sol_ser?.length ? authorized_sol_ser.some(s => solSer.includes(s)) : true; // 허용된 솔루션 서비스코드에 해당하는지

  // unrecognized 목록이 없으면 기본 허용(true)
  const isAllowedAff    = unrecognized_aff_no?.length  ? !unrecognized_aff_no.includes(affNo)                 : true; // 차단된 제휴사번호가 아닌지
  const isAllowedAffSer = unrecognized_aff_ser?.length ? !unrecognized_aff_ser.some(s => affSer.includes(s))  : true; // 차단된 제휴사 서비스코드가 아닌지

  // authorized 조건 상관없이 접근 허용 대상자
  const isExempted = exempted_m_no?.length ? exempted_m_no.includes(memberNo) : false;

  // 하나라도 authorized 조건을 만족하거나 제한이 없고, 차단 대상이 아닌 경우 접근 허용
  const isAuthorized = hasNoRestriction || isAuthorizedMember || isAuthorizedAffiliate || isAuthorizedAffSer || isAuthorizedSolSer;

  return (isAuthorized && isAllowedAff && isAllowedAffSer) || isExempted;
}